import type { MutationFunctionContext, QueryClient } from "@tanstack/react-query";
import type { TellescopeMutationInternalContext, TellescopeMutationSideEffectStrategy } from "../../hooks";
import type {
  ModelName,
  MutationMethodForModel,
  ParamsForMethod,
  QueryMethodForModel,
  ReturnForMethod,
  SupportedSession,
} from "../../types";
import { getAutomatedMutationSideEffects } from "./get-automated-mutation-side-effects";
import { parseISOStrings } from "@tellescope/type-correction";
import { formResponsePredicate } from "../../utils";

export interface ExecuteTellescopeMutationSideEffectArgs<
  Model extends ModelName,
  Method extends MutationMethodForModel<Model>,
  Strategy extends TellescopeMutationSideEffectStrategy,
> {
  queryClient: QueryClient;
  model: Model;
  method: Method;
  request: ParamsForMethod<Model, Method>;
  response?: ReturnForMethod<Model, Method>;
  sideEffect: "onMutate" | "onSuccess";
  strategy: Strategy;
  onMutateResult?: TellescopeMutationInternalContext<Model, QueryMethodForModel<Model>>;
  context: MutationFunctionContext;
  session?: SupportedSession;
}

/**
 * Executes automatic tanstack query cache updates for `onMutate` and `onSuccess` determined by the configuration
 * found in AUTOMATIC_MUTATION_SIDE_EFFECT_CONFIGS
 *
 * @template Model - The Tellescope model name (e.g., "users", "appointments")
 * @template Method - The mutation method for the model (e.g., "create_user", "update_appointment")
 * @template Strategy - The configured side effect strategy for the mutation
 *
 * @param args - Configuration object
 * @param args.queryClient - The Tanstack query client
 * @param args.model - The Tellescope model name
 * @param args.method - The mutation method to generate side effects for
 * @param args.request - The request object context for the mutation
 * @param [args.response] - The response from the endpoint called by the mutation. Only used by `onSuccess`
 * @param args.sideEffect - The side effect function to execute. `onMutate` or `onSuccess`.
 * @param args.strategy - The configured side effect strategy for the mutation. `"disabled"`, `"invalidate"`, or `"update"`
 * @param args.onMutateResult - The return from the onMutate function
 * @param args.context - The context from the mutation function
 *
 * @returns Either `undefined` for `onSuccess` or TellescopeMutationInternalContext for `onMutate`
 */
export const executeTellescopeMutationSideEffect = <
  Model extends ModelName,
  Method extends MutationMethodForModel<Model>,
  Strategy extends TellescopeMutationSideEffectStrategy,
>({
  queryClient,
  model,
  method,
  request,
  response,
  sideEffect,
  strategy,
  onMutateResult,
  context,
  session,
}: ExecuteTellescopeMutationSideEffectArgs<Model, Method, Strategy>): Strategy extends "update"
  ? TellescopeMutationInternalContext<Model, QueryMethodForModel<Model>>
  : undefined => {
  type ReturnType = Strategy extends "update"
    ? TellescopeMutationInternalContext<Model, QueryMethodForModel<Model>>
    : undefined;
  switch (strategy) {
    case "disabled":
      return undefined as ReturnType;
    case "invalidate":
    case "update": {
      const sideEffectFnConstructor = getAutomatedMutationSideEffects<Model, Method>({
        model,
        method,
      });
      switch (sideEffect) {
        case "onMutate": {
          const sideEffectConfigContext = session ? { session } : undefined;
          const sideEffectFn = sideEffectFnConstructor?.onMutate?.(queryClient, sideEffectConfigContext);
          // Parse ISO strings in request to ensure Date fields are properly typed during optimistic updates
          const parsedRequest = parseISOStrings(request, {
            keepType: true,
            deep: true,
            deepArrays: true,
            predicate: model === "form_responses" ? formResponsePredicate : undefined,
          });
          return sideEffectFn?.(parsedRequest, context) as ReturnType;
        }
        case "onSuccess": {
          if (response === undefined) return undefined as ReturnType;
          const sideEffectConfigContext = session ? { session } : undefined;
          const sideEffectFn = sideEffectFnConstructor?.onSuccess?.(queryClient, strategy, sideEffectConfigContext);
          // Parse ISO strings in request to ensure Date fields are properly typed during cache updates
          const parsedRequest = parseISOStrings(request, {
            keepType: true,
            deep: true,
            deepArrays: true,
            predicate: model === "form_responses" ? formResponsePredicate : undefined,
          });

          // Invalidate associated count queries
          queryClient.invalidateQueries({
            predicate: ({ queryKey }) => {
              if (!Array.isArray(queryKey) || queryKey.length < 2) return false;
              const queryModel = queryKey[0];
              if (typeof queryModel !== "string" || queryModel !== model) return false;
              const queryMethod = queryKey[1];
              if (typeof queryMethod !== "string" || !queryMethod.endsWith("-count")) return false;
              return true;
            },
          });
          return sideEffectFn?.(
            response,
            parsedRequest,
            // @ts-expect-error type is too broad here
            onMutateResult ?? { originalQueries: [] },
            context
          ) as ReturnType;
        }
      }
    }
  }
};
